import React, { useEffect, useMemo, useState } from "react";
import api from "@/api";
import SearchSelect from "../pages/SearchSelect";
import { CO_LABEL, coLabel, sortContracts } from "../utils/contractLabel";

const getId = (c) => c?.id;

/**
 * New project, or (with `project`) an existing one edited. A project belongs to a PO (contract): pick one, or pass
 * `contract` to fix it (e.g. from the trial wizard, which has chosen the PO already). onSaved(project) gets the saved
 * project. An edit is confirmed first (customer point 69): how many trials the project has and, when its CO changes,
 * whether its trials with the old CO get the new one (a trial with a CO of its own is never changed; trial numbers
 * are never renamed).
 */
export default function ProjectModal({ open, onClose, onSaved, contract = null, project = null }) {
  const [protocolno, setProtocolno] = useState("");
  const [contractId, setContractId] = useState(null);
  const [contracts, setContracts] = useState([]);
  const [loadingContracts, setLoadingContracts] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  // The edit to confirm: { protocolno, contractId, preview } (preview from change-preview), or null
  const [confirm, setConfirm] = useState(null);

  useEffect(() => {
    if (!open || !project) return;
    setProtocolno(project.protocolno ?? "");
    setContractId(project.contract_id ?? project.contract?.id ?? null);
    setConfirm(null);
    setError(null);
  }, [open, project]);

  useEffect(() => {
    if (!open || contract) return;
    setLoadingContracts(true);
    api
      .post("/masterdata/contracts")
      .then((res) => setContracts(Array.isArray(res.data) ? res.data : []))
      .catch(() => setContracts([]))
      .finally(() => setLoadingContracts(false));
  }, [open, contract]);

  const sortedContracts = useMemo(() => sortContracts(contracts), [contracts]);

  function reset() {
    setProtocolno("");
    setContractId(null);
    setError(null);
    setConfirm(null);
  }

  function save(body) {
    setSaving(true);
    setError(null);
    api
      .post("/masterdata/projects/store", body)
      .then((res) => {
        if (onSaved) onSaved(res?.data?.project ?? null, res?.data);
        reset();
        onClose();
      })
      .catch((err) => {
        setError(err?.response?.data?.message || "Failed to save project");
      })
      .finally(() => setSaving(false));
  }

  // An edit: unchanged closes; else the confirmation, with what a change of CO does to the trials
  function confirmEdit(no, poId) {
    if (no === project.protocolno && Number(poId) === Number(project.contract_id)) {
      handleClose();
      return;
    }
    setSaving(true);
    setError(null);
    api
      .post(`/masterdata/projects/${project.id}/change-preview`, { contract_id: poId })
      .then((res) => setConfirm({ protocolno: no, contractId: poId, preview: res.data }))
      .catch((err) => setError(err?.response?.data?.message || "Failed to check the project's trials"))
      .finally(() => setSaving(false));
  }

  function handleClose() {
    if (saving) return;
    reset();
    onClose();
  }

  async function handleSubmit(e) {
    e.preventDefault();
    const poId = contract?.id ?? contractId;
    if (!protocolno.trim()) {
      setError("Protocol number is required");
      return;
    }
    if (!poId) {
      setError(`Select the ${CO_LABEL} the project belongs to`);
      return;
    }
    if (project) {
      confirmEdit(protocolno.trim(), poId);
      return;
    }
    save({ protocolno: protocolno.trim(), contract_id: poId });
  }

  if (!open) return null;
  if (confirm) {
    const { preview } = confirm;
    const follow = preview.co_changes && preview.with_old_co > 0;
    const ownCo = preview.trials - preview.with_old_co;
    const body = (trialsFollow) => ({ id: project.id, protocolno: confirm.protocolno, contract_id: confirm.contractId, trials_follow: trialsFollow });
    const trialWord = (n) => `${n} ${n === 1 ? "trial" : "trials"}`;
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onMouseDown={handleClose}>
        <div
          role="dialog"
          aria-label="Confirm project change"
          className="bg-white dark:bg-gray-900 rounded-lg shadow-xl w-full max-w-md"
          onMouseDown={(e) => e.stopPropagation()}
        >
          <div className="px-5 py-4 border-b border-gray-200 dark:border-gray-700">
            <h2 className="text-lg font-semibold text-emerald-700 dark:text-emerald-300">Change project {project.protocolno}?</h2>
          </div>
          <div className="px-5 py-4 space-y-3 text-sm" data-testid="project-change-summary">
            <ul className="list-disc pl-5 space-y-1">
              {confirm.protocolno !== project.protocolno && (
                <li>Protocol No: {project.protocolno} → {confirm.protocolno}. Trial numbers are not renamed.</li>
              )}
              {preview.co_changes && <li>{CO_LABEL}: {preview.old_co ?? "—"} → {preview.new_co}</li>}
            </ul>
            <p>The project has {trialWord(preview.trials)}.</p>
            {follow && (
              <>
                <p className="font-medium" data-testid="project-change-follow">
                  {trialWord(preview.with_old_co)} {preview.with_old_co === 1 ? "has" : "have"} {CO_LABEL} {preview.old_co}. Should {preview.with_old_co === 1 ? "it" : "they"} get {CO_LABEL} {preview.new_co} too?
                </p>
                {ownCo > 0 && <p>{trialWord(ownCo)} with a {CO_LABEL} of {ownCo === 1 ? "its" : "their"} own {ownCo === 1 ? "is" : "are"} not changed.</p>}
                {preview.other_customer > 0 && (
                  <p role="note" className="rounded border border-amber-300 bg-amber-50 px-3 py-2 text-amber-800 dark:border-amber-700 dark:bg-amber-950/30 dark:text-amber-200">
                    {preview.other_customer === 1 ? "1 of them belongs" : `${preview.other_customer} of them belong`} to another customer than {CO_LABEL} {preview.new_co}.
                  </p>
                )}
              </>
            )}
            {error && <div role="alert" className="text-red-600 dark:text-red-400">{error}</div>}
          </div>
          <div className="flex flex-wrap items-center justify-end gap-3 px-5 pb-4">
            <button type="button" onClick={() => setConfirm(null)} className="px-4 py-2 rounded border border-gray-300 dark:border-gray-600 text-sm">
              Cancel
            </button>
            {follow ? (
              <>
                <button type="button" disabled={saving} onClick={() => save(body(false))} className="px-4 py-2 rounded border border-emerald-600 text-emerald-700 dark:text-emerald-300 text-sm font-medium disabled:opacity-60">
                  Save, trials keep {CO_LABEL} {preview.old_co}
                </button>
                <button type="button" disabled={saving} onClick={() => save(body(true))} className="px-4 py-2 rounded bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium disabled:opacity-60">
                  Save, trials get {CO_LABEL} {preview.new_co}
                </button>
              </>
            ) : (
              <button type="button" disabled={saving} onClick={() => save(body(false))} className="px-5 py-2 rounded bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium disabled:opacity-60">
                Save Project
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onMouseDown={handleClose}
    >
      <div
        role="dialog"
        aria-label={project ? "Edit project" : "New project"}
        className="bg-white dark:bg-gray-900 rounded-lg shadow-xl w-full max-w-md"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="px-5 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-emerald-700 dark:text-emerald-300">
            {project ? "Edit Project" : "New Project"}
          </h2>
          <button
            onClick={handleClose}
            aria-label="Close"
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          >
            ✕
          </button>
        </div>
        <form onSubmit={handleSubmit} className="px-5 py-4 space-y-4">
          <div>
            <label htmlFor="project-protocolno" className="block text-sm font-medium mb-1">
              Protocol Number<span className="text-red-500">*</span>
            </label>
            <input
              id="project-protocolno"
              className="w-full rounded border px-3 py-2 bg-white dark:bg-gray-800 dark:border-gray-700"
              value={protocolno}
              onChange={(e) => setProtocolno(e.target.value)}
              placeholder="e.g. 67 15 04"
              required
              autoFocus
            />
          </div>
          <div>
            <p id="project-po-label" className="block text-sm font-medium mb-1">
              {CO_LABEL}<span className="text-red-500">*</span>
            </p>
            {contract ? (
              <p className="rounded border px-3 py-2 bg-gray-100 text-gray-700 dark:bg-gray-800 dark:border-gray-700 dark:text-gray-300">
                {coLabel(contract)}
              </p>
            ) : (
              <div aria-labelledby="project-po-label">
                <SearchSelect
                  options={sortedContracts}
                  value={contractId}
                  onChange={(c) => setContractId(c ? c.id : null)}
                  getOptionLabel={coLabel}
                  getOptionValue={getId}
                  placeholder={loadingContracts ? "Loading..." : "Search number or description..."}
                  emptyText="No match"
                  disabled={loadingContracts}
                />
              </div>
            )}
          </div>
          {error && (
            <div role="alert" className="text-sm text-red-600 dark:text-red-400">
              {error}
            </div>
          )}
          <div className="flex items-center justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={handleClose}
              className="px-4 py-2 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 text-sm"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-5 py-2 rounded bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white text-sm font-medium"
            >
              {saving ? "Saving..." : "Save Project"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
